import { useState } from "react";
import { Button, Menu, MenuItem, ButtonGroup } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { Run } from "../../../services/appsync/types";
import { useMemo } from "react";

export type StartRunDropdownOption = {
  label: string;
  onClick: () => void;
};

export const StartRunDropdownComponent = ({
  engineInProgress,
  runInProgress,
  options,
  onRunWithLockedForecast,
  onRunWithRecalculatedForecast,
  loading,
}: {
  engineInProgress: boolean;
  runInProgress: Run | null;
  options?: StartRunDropdownOption[];
  onRunWithLockedForecast?: () => void;
  onRunWithRecalculatedForecast?: () => void;
  loading: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const resolvedOptions: StartRunDropdownOption[] = useMemo(() => {
    if (options && options.length > 0) {
      return options;
    }
    const fallback: StartRunDropdownOption[] = [];
    if (onRunWithLockedForecast) {
      fallback.push({
        label: "Run with locked forecast",
        onClick: onRunWithLockedForecast,
      });
    }
    if (onRunWithRecalculatedForecast) {
      fallback.push({
        label: "Run with recalculated forecast",
        onClick: onRunWithRecalculatedForecast,
      });
    }
    return fallback;
  }, [options, onRunWithLockedForecast, onRunWithRecalculatedForecast]);

  const buttonLabel = useMemo(() => {
    if (engineInProgress && runInProgress) {
      if (runInProgress?.runSettings.workflowId === "workflow_atomic_pivots") {
        return `Running ... ${runInProgress?.atStep! / runInProgress?.totalSteps! ? `${Math.round((runInProgress?.atStep! / runInProgress?.totalSteps!) * 100)}%` : ""}`;
      } else if (
        runInProgress?.runSettings.workflowId === "google_drive_sync"
      ) {
        return `Data sync ...`;
      }
    }
    return "Start run";
  }, [engineInProgress, runInProgress]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (engineInProgress || loading) {
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOptionClick = (option: StartRunDropdownOption) => () => {
    option.onClick();
    handleClose();
  };

  return (
    <>
      <ButtonGroup
        variant={engineInProgress ? "outlined" : "contained"}
        sx={{ mr: 1 }}
      >
        <Button onClick={handleClick} disabled={engineInProgress || loading}>
          {buttonLabel}
        </Button>
        <Button
          onClick={handleClick}
          disabled={engineInProgress || loading}
          sx={{ minWidth: "auto", px: 1 }}
        >
          <ArrowDropDown />
        </Button>
      </ButtonGroup>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        {resolvedOptions.map((option) => (
          <MenuItem key={option.label} onClick={handleOptionClick(option)}>
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
